import { useCallback } from "react";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";

import Avatars from "@components/avatar/avatars";
import { classNames } from "@helpers/css";

import { AssessmentAnswerWithResponderAndId } from "./questions/types";

const AssessmentQuestionMultiSelectOptions = ({
  disabled,
  choices,
  options,
  optionDescriptions,
  otherParticipantsAnswers,
  onClickAnswer,
}: {
  choices?: ReadonlyArray<number | null> | null;
  options: ReadonlyArray<string | null>;

  otherParticipantsAnswers?: AssessmentAnswerWithResponderAndId[];
  optionDescriptions: ReadonlyArray<string | null>;
  disabled?: boolean;
  onClickAnswer?: (choices: number[]) => void;
}) => {
  const handleClickAnswer = useCallback(
    (index: number) => {
      if (disabled) {
        return;
      }
      if (onClickAnswer) {
        if (choices?.includes(index)) {
          onClickAnswer(
            choices
              .flatMap((f) => (f !== null ? [f] : []))
              .filter((choice) => choice !== index),
          );
        } else {
          onClickAnswer(
            choices
              ? choices.flatMap((f) => (f !== null ? [f] : [])).concat(index)
              : [index],
          );
        }
      }
    },
    [choices, disabled, onClickAnswer],
  );

  return (
    <div className="flex flex-col gap-3">
      {options.map((value, index) => {
        const isChecked = choices?.includes(index);
        const Icon = isChecked ? MdCheckBox : MdCheckBoxOutlineBlank;
        return (
          <div
            className={classNames(
              "border rounded-md py-2.5 px-3 flex items-start justify-between gap-4",
              isChecked && "bg-blue-100 border-transparent",
              !isChecked && "bg-gray-50 border-gray-100",
              disabled && "cursor-default",
              !disabled && "cursor-pointer",
              !disabled && !isChecked && "hover:bg-gray-200",
            )}
            key={value}
            role="button"
            onClick={() => {
              handleClickAnswer(index);
            }}
          >
            <div className="flex items-center gap-4">
              <Icon
                className={classNames(
                  "shrink-0 w-5 h-5",
                  disabled && "text-gray-500",
                  !disabled && "text-blue-700",
                )}
              />
              <div className="text-sm">
                <div>{value}</div>
                {optionDescriptions[index] && (
                  <div className="italic text-gray-500 whitespace-pre-line">
                    {optionDescriptions[index]}
                  </div>
                )}
              </div>
            </div>
            {otherParticipantsAnswers &&
              otherParticipantsAnswers.filter((answer) =>
                answer.choices?.includes(index),
              ).length > 0 && (
                <Avatars
                  showNullAsAnonymous
                  users={otherParticipantsAnswers
                    .filter((answer) => answer.choices?.includes(index))
                    .map((answer) => answer.responder)}
                  size={5}
                  max={3}
                  modalTitle="Responders"
                />
              )}
          </div>
        );
      })}
    </div>
  );
};

export default AssessmentQuestionMultiSelectOptions;
